/**
 * Le dernier jeu de réglages connu, gardé dans le navigateur.
 *
 * ## Le problème qu'il résout
 *
 * Les réglages arrivent d'une requête. Le premier rendu, lui, est immédiat :
 * entre les deux, un écran doit bien afficher quelque chose. Tant que ce
 * « quelque chose » était la valeur par défaut, chaque visiteur voyait l'image
 * d'avant une fraction de seconde, puis celle d'aujourd'hui — un clignotement
 * à chaque arrivée sur le site, d'autant plus visible que l'ancienne image est
 * déjà dans le cache du navigateur quand la nouvelle doit être téléchargée.
 *
 * Le remède tient en une phrase : se souvenir. Quelqu'un qui est déjà venu a
 * la bonne adresse dès le premier rendu, et le navigateur commence à
 * télécharger la bonne image sans attendre la requête.
 *
 * ## Pourquoi le cache n'est jamais cru sur parole
 *
 * Ce que l'on relit a pu être écrit par une version plus ancienne du site, ou
 * modifié à la main. Tout repasse donc par `versReglages`, exactement comme ce
 * qui vient de la base : un champ abîmé retombe sur son défaut plutôt que de
 * produire une image cassée.
 *
 * ## Pourquoi une date limite
 *
 * Une adresse de photo peut disparaître — image remplacée, seau nettoyé. Après
 * un mois sans visite, mieux vaut attendre la requête que peindre une adresse
 * dont plus rien ne garantit qu'elle réponde.
 *
 * ## Pourquoi un rangement passé en argument
 *
 * `localStorage` n'existe pas partout : navigation privée, stockage refusé,
 * et pas du tout côté Node — où tournent les tests. Le module ne le cherche
 * jamais lui-même ; `rangementDuNavigateur()` le trouve s'il est là, et rend
 * `null` sinon. Aucun appel ne peut donc faire tomber une page.
 */

import { versReglages, type Reglages } from './index';

/** La version est dans la clé : changer la forme des réglages périme le cache. */
export const CLE_CACHE = 'eclosion.reglages.v1';

export const JOURS_DE_VALIDITE = 30;

/** Le strict nécessaire de `Storage` — de quoi brancher un faux dans un test. */
export interface Rangement {
  getItem(cle: string): string | null;
  setItem(cle: string, valeur: string): void;
  removeItem(cle: string): void;
}

/**
 * Le `localStorage` du navigateur, ou `null`.
 *
 * Y toucher lève dans certains navigateurs quand le stockage est refusé : le
 * simple fait de le lire est donc déjà protégé.
 */
export function rangementDuNavigateur(): Rangement | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * Les réglages retenus lors de la dernière visite, ou `null`.
 *
 * `null` veut dire « on ne sait pas encore » — jamais « voici les valeurs par
 * défaut ». C'est toute la différence : l'appelant qui ne sait pas peut le
 * dire, au lieu d'afficher une ancienne photo comme si c'était la bonne.
 */
export function lireCache(rangement: Rangement | null, maintenant: Date = new Date()): Reglages | null {
  if (!rangement) return null;
  try {
    const brut = rangement.getItem(CLE_CACHE);
    if (!brut) return null;

    const enveloppe = JSON.parse(brut) as { le?: unknown; reglages?: unknown };
    const le = typeof enveloppe?.le === 'string' ? Date.parse(enveloppe.le) : NaN;
    if (Number.isNaN(le)) return null;

    const jours = (maintenant.getTime() - le) / 86_400_000;
    // Une date dans le futur trahit une horloge remise a l'heure : on repart de
    // la requete plutot que de garder un cache qui ne perimera jamais.
    if (jours < 0 || jours > JOURS_DE_VALIDITE) return null;

    if (!enveloppe.reglages || typeof enveloppe.reglages !== 'object') return null;
    return versReglages(enveloppe.reglages);
  } catch {
    return null;
  }
}

/** Retient les réglages pour la prochaine visite. Un échec ne se voit pas. */
export function ecrireCache(
  reglages: Reglages,
  rangement: Rangement | null,
  maintenant: Date = new Date(),
): void {
  if (!rangement) return;
  try {
    rangement.setItem(CLE_CACHE, JSON.stringify({ le: maintenant.toISOString(), reglages }));
  } catch {
    /* Quota plein ou stockage refusé : le site marche sans, en affichant le
       cadre neutre le temps de la requête. Rien à signaler à qui que ce soit. */
  }
}
